import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import storyRunService from '../service/story-run.service';
import storyImagePlanService from '../service/story-image-plan.service';
import upscaleService from '../service/upscale.service';
import TtsStepEditModal from '../components/TtsStepEditModal';
import ImageCropDialog from '../components/ImageCropDialog';
import ScenePlanForm from '../components/ScenePlanForm';
import TtsStepsForm from '../components/TtsStepsForm';
import SceneAdvancedSettings from '../components/SceneAdvancedSettings';
import GlobalRenderSettingsForm from '../components/GlobalRenderSettingsForm';
import storyBaseService from '../service/story-base.service';

const formatMs = (ms?: number) => {
  if (!ms && ms !== 0) return '';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
};

const RunDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [run, setRun] = React.useState<any | null>(null);
  const [plans, setPlans] = React.useState<any[]>([]);
  const [editingIdx, setEditingIdx] = React.useState<number | null>(null);
  const [planBackup, setPlanBackup] = React.useState<any | null>(null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [messageOpen, setMessageOpen] = React.useState(false);
  const [messageText, setMessageText] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [start2Open, setStart2Open] = React.useState(false);
  const [start2Loading, setStart2Loading] = React.useState(false);
  const [precheckLoading, setPrecheckLoading] = React.useState(false);
  const [precheckHasData, setPrecheckHasData] = React.useState(false);
  const [audioMap, setAudioMap] = React.useState<Record<number, string | null>>({});
  const [descriptionMap, setDescriptionMap] = React.useState<Record<number, string>>({});
  const [thumbMap, setThumbMap] = React.useState<Record<number, string | null>>({});
  const [imageMap, setImageMap] = React.useState<Record<number, string | null>>({});
  const [planIdMap, setPlanIdMap] = React.useState<Record<number, string>>({});
  const [upscaledMap, setUpscaledMap] = React.useState<Record<number, string | null>>({});
  const [userThumbMap, setUserThumbMap] = React.useState<Record<number, string | null>>({});
  const [userImageMap, setUserImageMap] = React.useState<Record<number, string | null>>({});
  const [userLocalPreviewMap, setUserLocalPreviewMap] = React.useState<Record<number, string>>({});
  const [userLocalFileMap, setUserLocalFileMap] = React.useState<Record<number, File>>({});
  const [imgPreviewOpen, setImgPreviewOpen] = React.useState(false);
  const [imgPreviewUrl, setImgPreviewUrl] = React.useState<string | null>(null);
  // const [playingIdx, setPlayingIdx] = React.useState<number | null>(null);
  const audioRefs = React.useRef<Record<number, HTMLAudioElement | null>>({});
  const [audioDurMap, setAudioDurMap] = React.useState<Record<number, number>>({});
  const [audioPosMap, setAudioPosMap] = React.useState<Record<number, number>>({});
  const [aiBusyMap, setAiBusyMap] = React.useState<Record<number, boolean>>({});
  const [ttsStepsMap, setTtsStepsMap] = React.useState<Record<number, any[]>>({});
  const [redoBusy, setRedoBusy] = React.useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = React.useState<boolean>(false);
  const [editModalStep, setEditModalStep] = React.useState<any | null>(null);
  // Controlled accordion expanded index (single open)
  const [expandedIdx, setExpandedIdx] = React.useState<number | null>(null);
  // Global render settings accordion
  const [globalExpanded, setGlobalExpanded] = React.useState<boolean>(false);
  // Story target (youtube | short | other)
  const [storyTarget, setStoryTarget] = React.useState<string | null>(null);

  const withBust = React.useCallback((u?: string | null): string | null => {
    if (!u) return null;
    const sep = u.includes('?') ? '&' : '?';
    return `${u}${sep}v=${Date.now()}`;
  }, []);

  // TTS one-dialog confirmation state
  const [ttsOpen, setTtsOpen] = React.useState(false);
  const [ttsBusy, setTtsBusy] = React.useState(false);
  const [ttsIndex, setTtsIndex] = React.useState<number | null>(null);
  const [ttsHasAudio, setTtsHasAudio] = React.useState<boolean>(false);
  // Per-scene TTS controls
  const [speakingRateMap, setSpeakingRateMap] = React.useState<Record<number, string>>({});
  const [stabilityMap, setStabilityMap] = React.useState<Record<number, string>>({});
  // Step-level re-tts confirm state
  const [redoStepOpen, setRedoStepOpen] = React.useState(false);
  const [redoStepBusy, setRedoStepBusy] = React.useState(false);
  const [redoStep, setRedoStep] = React.useState<{ stepId: string; oi: number } | null>(null);

  // Image crop dialog state (16:9)
  const [cropOpen, setCropOpen] = React.useState<boolean>(false);
  const [cropImageUrl, setCropImageUrl] = React.useState<string | null>(null);
  const [cropIndex, setCropIndex] = React.useState<number | null>(null);

  const formatTime = (sec?: number) => {
    if (!sec && sec !== 0) return '';
    const s = Math.max(0, Math.floor(sec));
    const m = Math.floor(s / 60);
    const r = s % 60;
    const mm = String(m);
    const ss = String(r).padStart(2, '0');
    return `${mm}:${ss}`;
  };

  // Upscale confirm state
  const [upOpen, setUpOpen] = React.useState(false);
  const [upBusy, setUpBusy] = React.useState(false);
  const [upIndex, setUpIndex] = React.useState<number | null>(null);
  const [upHasExisting, setUpHasExisting] = React.useState<boolean>(false);
  const [upHasBaseImage, setUpHasBaseImage] = React.useState<boolean>(false);

  // AI Create Image confirm state
  const [aiCreateOpen, setAiCreateOpen] = React.useState(false);
  const [aiCreateBusy, setAiCreateBusy] = React.useState(false);
  const [aiCreateIndex, setAiCreateIndex] = React.useState<number | null>(null);

  // Edit form state
  // const [formStatus, setFormStatus] = React.useState<string>('');
  const [formMode, setFormMode] = React.useState<string>('');
  const [formDuration, setFormDuration] = React.useState<string>('');
  // const [formImageCount, setFormImageCount] = React.useState<string>('');
  const [formBaseImageUrl, setFormBaseImageUrl] = React.useState<string>('');
  const [formUserNote, setFormUserNote] = React.useState<string>('');

  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const data = await storyRunService.getRunById(id);
        if (!alive) return;
        setRun(data);
        setPlans(Array.isArray(data.imagePlanJson) ? data.imagePlanJson : []);
        // fetch DB plans for audioUrl
        try {
          const plansDb = await storyImagePlanService.getPlansByRunId(id);
          const aMap: Record<number, string | null> = {};
          const dMap: Record<number, string> = {};
          const tMap: Record<number, string | null> = {};
          const iMap: Record<number, string | null> = {};
          const pidMap: Record<number, string> = {};
          const uMap: Record<number, string | null> = {};
          const utMap: Record<number, string | null> = {};
          const uiMap: Record<number, string | null> = {};
          const stepsMap: Record<number, any[]> = {};
          for (const raw of Array.isArray(plansDb) ? plansDb : []) {
            const p: any = raw as any;
            if (typeof p?.orderIndex === 'number') {
              const k0 = p.orderIndex;
              const k1 = p.orderIndex + 1;
              // Map to both keys to be robust against 0-based/1-based differences
              for (const k of [k0, k1]) {
                pidMap[k] = p.id;
                aMap[k] = (p?.audioUrl as string) || null;
                if (typeof p?.description === 'string') dMap[k] = p.description;
                tMap[k] = withBust((p?.imageThumbnailUrl as string) || null);
                iMap[k] = withBust((p?.imageUrl as string) || null);
                uMap[k] = withBust((p?.upscaledImageUrl as string) || null);
                utMap[k] = withBust((p?.userImageThumbnailUrl as string) || null);
                uiMap[k] = withBust((p?.userImageUrl as string) || null);
                const steps = Array.isArray((p as any).ttsSteps) ? (p as any).ttsSteps : [];
                stepsMap[k] = steps;
              }
            }
          }
          setPlanIdMap(pidMap);
          setAudioMap(aMap);
          setDescriptionMap(dMap);
          setThumbMap(tMap);
          setImageMap(iMap);
          setUpscaledMap(uMap);
          setUserThumbMap(utMap);
          setUserImageMap(uiMap);
          setTtsStepsMap(stepsMap);
        } catch {}
        // Prefill form
        setFormMode(String(data.mode || ''));
        setFormDuration(typeof data.estimatedDurationMs === 'number' ? String(data.estimatedDurationMs) : '');
        setFormBaseImageUrl(String(data.baseImageUrl || ''));
        setFormUserNote(String(data.userNote || ''));
      } catch (e: any) {
        if (!alive) return;
        setError(e?.response?.data?.message || e?.message || 'Run detayları yüklenemedi');
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [id, withBust]);

  // Load story base to get target (short/youtube)
  React.useEffect(() => {
    let alive = true;
    const loadBase = async () => {
      const baseId = run?.storyBaseId;
      if (!baseId) return;
      try {
        const base = await storyBaseService.getStoryBase(baseId);
        if (!alive) return;
        setStoryTarget((base as any)?.target || null);
      } catch {}
    };
    loadBase();
    return () => { alive = false; };
  }, [run?.storyBaseId]);

  React.useEffect(() => {
    let alive = true;
    const runId = run?.id;
    if (start2Open && runId) {
      setPrecheckLoading(true);
      setPrecheckHasData(false);
      storyImagePlanService.getPlansByRunId(runId)
        .then((data) => {
          if (!alive) return;
          setPrecheckHasData(Array.isArray(data) && data.length > 0);
        })
        .catch(() => {
          if (!alive) return;
        })
        .finally(() => {
          if (!alive) return;
          setPrecheckLoading(false);
        });
    }
    return () => { alive = false; };
  }, [start2Open, run?.id]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Run Detail</Typography>
        <Box>
          <IconButton size="small" onClick={() => setEditOpen(true)} aria-label="Edit run" sx={{ mr: 0.5 }}>
            <EditIcon />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => setDeleteOpen(true)} aria-label="Delete run" sx={{ mr: 1 }}>
            <DeleteIcon />
          </IconButton>
          <Button size="small" onClick={() => navigate(-1)}>Geri</Button>
        </Box>
      </Box>
      {loading && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 1 }}>Yükleniyor...</Typography>
        </Stack>
      )}

      {/* Edit Run Dialog */}
      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Update Run</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">Status ve Image Count düzenlenemez.</Alert>
            <TextField label="Mode" value={formMode} onChange={(e) => setFormMode(e.target.value)} select>
              {['dialog','single'].map((m) => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </TextField>
            <TextField label="Estimated Duration (ms)" type="number" value={formDuration} onChange={(e) => setFormDuration(e.target.value)} />
            <TextField label="Base Image URL" value={formBaseImageUrl} onChange={(e) => setFormBaseImageUrl(e.target.value)} />
            <TextField label="User Note" value={formUserNote} onChange={(e) => setFormUserNote(e.target.value)} multiline minRows={3} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving || !id}
            onClick={async () => {
              if (!id) return;
              setSaving(true);
              try {
                const payload: any = {
                  // status immutable per request
                  mode: formMode || undefined,
                  estimatedDurationMs: formDuration ? Number(formDuration) : null,
                  // imageCount immutable per request
                  baseImageUrl: formBaseImageUrl || null,
                  userNote: formUserNote || null,
                };
                const res = await storyRunService.updateRun(id, payload);
                setMessageText(res?.message || 'Run updated');
                setMessageOpen(true);
                // refetch
                try {
                  const fresh = await storyRunService.getRunById(id);
                  setRun(fresh);
                  setPlans(Array.isArray(fresh.imagePlanJson) ? fresh.imagePlanJson : []);
                } catch {}
                setEditOpen(false);
              } catch (e: any) {
                setMessageText(e?.response?.data?.message || e?.message || 'Güncelleme başarısız');
                setMessageOpen(true);
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Create Image Confirm */}
      <Dialog open={aiCreateOpen} onClose={() => !aiCreateBusy && setAiCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>AI Görsel Oluştur</DialogTitle>
        <DialogContent dividers>
          <Typography>Bu sahne için AI ile görsel oluşturmak istiyor musunuz?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiCreateOpen(false)} disabled={aiCreateBusy}>Vazgeç</Button>
          <Button
            variant="contained"
            disabled={aiCreateBusy || !aiCreateIndex || !planIdMap[aiCreateIndex]}
            onClick={async () => {
              const oi = aiCreateIndex as number;
              const planId = planIdMap[oi];
              if (!planId) return;
              setAiCreateBusy(true);
              setAiBusyMap((prev) => ({ ...prev, [oi]: true }));
              try {
                const updated = await storyImagePlanService.createFromAi(planId, oi);
                setThumbMap((prev) => ({ ...prev, [oi]: withBust(updated.imageThumbnailUrl || null) }));
                setImageMap((prev) => ({ ...prev, [oi]: withBust(updated.imageUrl || null) }));
                setMessageText('AI image created');
                setMessageOpen(true);
                setAiCreateOpen(false);
                setAiBusyMap((prev) => ({ ...prev, [oi]: false }));
              } catch (e: any) {
                const msg = e?.response?.data?.message || e?.message || '';
                const isNet = !e?.response && (e?.message || '').toLowerCase().includes('network');
                if (isNet && run?.id) {
                  setMessageText('Ağ hatası: 2 dakika içinde işlem tamamlanabilir. Lütfen bekleyin, ardından sayfa otomatik yenilenecek.');
                  setMessageOpen(true);
                  // keep dialog open or close? Close to prevent duplicates
                  setAiCreateOpen(false);
                  setTimeout(async () => {
                    try {
                      const plansDb = await storyImagePlanService.getPlansByRunId(run.id!);
                      const aMap: Record<number, string | null> = {};
                      const dMap: Record<number, string> = {};
                      const tMap: Record<number, string | null> = {};
                      const iMap: Record<number, string | null> = {};
                      const pidMap: Record<number, string> = {};
                      const uMap: Record<number, string | null> = {};
                      const utMap: Record<number, string | null> = {};
                      const uiMap: Record<number, string | null> = {};
                      const stepsMap: Record<number, any[]> = {};
                      for (const raw of Array.isArray(plansDb) ? plansDb : []) {
                        const p: any = raw as any;
                        if (typeof p?.orderIndex === 'number') {
                          const k0 = p.orderIndex;
                          const k1 = p.orderIndex + 1;
                          for (const k of [k0, k1]) {
                            pidMap[k] = p.id;
                            aMap[k] = (p?.audioUrl as string) || null;
                            if (typeof p?.description === 'string') dMap[k] = p.description;
                            tMap[k] = withBust((p?.imageThumbnailUrl as string) || null);
                            iMap[k] = withBust((p?.imageUrl as string) || null);
                            uMap[k] = withBust((p?.upscaledImageUrl as string) || null);
                            utMap[k] = withBust((p?.userImageThumbnailUrl as string) || null);
                            uiMap[k] = withBust((p?.userImageUrl as string) || null);
                            const steps = Array.isArray((p as any).ttsSteps) ? (p as any).ttsSteps : [];
                            stepsMap[k] = steps;
                          }
                        }
                      }
                      setPlanIdMap(pidMap);
                      setAudioMap(aMap);
                      setDescriptionMap(dMap);
                      setThumbMap(tMap);
                      setImageMap(iMap);
                      setUpscaledMap(uMap);
                      setUserThumbMap(utMap);
                      setUserImageMap(uiMap);
                      setTtsStepsMap(stepsMap);
                      setMessageText('AI image durumu güncellendi');
                      setMessageOpen(true);
                    } catch {}
                    setAiBusyMap((prev) => ({ ...prev, [oi]: false }));
                    setAiCreateBusy(false);
                  }, 120000);
                } else {
                  setMessageText(msg || 'AI image create failed');
                  setMessageOpen(true);
                  setAiBusyMap((prev) => ({ ...prev, [oi]: false }));
                  setAiCreateBusy(false);
                }
              }
            }}
          >
            {aiCreateBusy ? 'Bekleyiniz...' : 'Evet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upscale Confirm Dialog */}
      <Dialog open={upOpen} onClose={() => !upBusy && setUpOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Upscale</DialogTitle>
        <DialogContent dividers>
          {!upHasBaseImage ? (
            <Alert severity="warning">
              {storyTarget === 'short'
                ? 'Önce bir resim yüklemelisiniz.'
                : 'Önce bir resim yüklemelisiniz (1536x864) ebatlarında.'}
            </Alert>
          ) : upHasExisting ? (
            <Alert severity="warning">
              Daha once resim Upscale yapildi. Yeniden yapilmasini istiyor musunuz?
            </Alert>
          ) : (
            <Typography>Upscale yapmak istediginizden emin misiniz?</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpOpen(false)} disabled={upBusy}>Vazgeç</Button>
          <Button
            variant="contained"
            disabled={upBusy || !upIndex || !upHasBaseImage}
            onClick={async () => {
              const oi = upIndex;
              if (!oi) return;
              const planId = planIdMap[oi];
              if (!planId) return;
              setUpBusy(true);
              try {
                const j = await upscaleService.upscalePlan(planId, 2);
                const msg = (j && typeof j.message === 'string' && j.message) || 'Upscale talebi gönderildi';
                setMessageText(msg);
                setMessageOpen(true);
                setUpOpen(false);
              } catch (e: any) {
                setMessageText(e?.message || 'Upscale başarısız');
                setMessageOpen(true);
              } finally {
                setUpBusy(false);
              }
            }}
          >
            {upBusy ? 'Bekleyiniz...' : 'Evet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Fullscreen Preview */}
      <Dialog open={imgPreviewOpen} onClose={() => setImgPreviewOpen(false)} fullScreen>
        <DialogTitle>Image Preview</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0 }}>
          {imgPreviewUrl && (
            <img src={imgPreviewUrl} alt="full" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImgPreviewOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* TTS One Dialog Confirm */}
      <Dialog open={ttsOpen} onClose={() => !ttsBusy && setTtsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Seslendirme</DialogTitle>
        <DialogContent dividers>
          {ttsHasAudio ? (
            <Alert severity="warning">
              Daha önce seslendirme yapılmış. Yeni seslendirme yaparsanız eskisi silinecek. Emin misiniz?
            </Alert>
          ) : (
            <Typography>Bu sahneyi seslendirmek istiyor musunuz?</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTtsOpen(false)} disabled={ttsBusy}>Vazgeç</Button>
          <Button
            variant="contained"
            disabled={ttsBusy || !run?.id || !ttsIndex}
            onClick={async () => {
              if (!run?.id || !ttsIndex) return;
              setTtsBusy(true);
              try {
                // First attempt: force=false
                const oi = ttsIndex;
                const srRaw = oi ? speakingRateMap[oi] : undefined;
                const stRaw = oi ? stabilityMap[oi] : undefined;
                let sr = srRaw ? parseFloat(srRaw) : NaN;
                let st = stRaw ? parseFloat(stRaw) : NaN;
                if (!Number.isFinite(sr)) sr = 0.75;
                if (!Number.isFinite(st)) st = 0.5;
                const payloadBase: any = { storyRunId: run.id, index: ttsIndex, force: false, speaking_rate: sr, stability: st };
                const res1 = await storyRunService.ttsDialogOne(payloadBase);
                if (res1?.processed) {
                  setAudioMap((prev) => ({ ...prev, [ttsIndex]: res1.audioUrl ?? null }));
                  setMessageText('Seslendirme tamamlandı');
                  setMessageOpen(true);
                  setTtsOpen(false);
                } else if (res1?.skipped && ttsHasAudio) {
                  // Already existed; since user confirmed, redo with force=true
                  const payload2: any = { storyRunId: run.id, index: ttsIndex, force: true, speaking_rate: sr, stability: st };
                  const res2 = await storyRunService.ttsDialogOne(payload2);
                  if (res2?.processed) {
                    setAudioMap((prev) => ({ ...prev, [ttsIndex]: res2.audioUrl ?? null }));
                    setMessageText('Seslendirme yenilendi');
                    setMessageOpen(true);
                  } else {
                    setMessageText('İşlem atlandı veya değişiklik gerekmiyor');
                    setMessageOpen(true);
                  }
                  setTtsOpen(false);
                } else {
                  // No audio and still skipped, or unexpected
                  setMessageText('İşlem atlandı veya değişiklik gerekmiyor');
                  setMessageOpen(true);
                  setTtsOpen(false);
                }
              } catch (e: any) {
                // If network error or backend says already in progress, start polling until completion
                const msg = e?.response?.data?.message || e?.message || '';
                const isNet = !e?.response && (e?.message || '').toLowerCase().includes('network');
                const isInProgress = typeof msg === 'string' && msg.toLowerCase().includes('zaten devam ediyor');
                if ((isNet || isInProgress) && run?.id && ttsIndex) {
                  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
                  let success = false;
                  for (let attempt = 0; attempt < 30; attempt++) { // ~90s max at 3s interval
                    try {
                      const oi = ttsIndex;
                      const srRaw = speakingRateMap[oi];
                      const stRaw = stabilityMap[oi];
                      let sr2 = srRaw ? parseFloat(srRaw) : NaN;
                      let st2 = stRaw ? parseFloat(stRaw) : NaN;
                      if (!Number.isFinite(sr2)) sr2 = 0.75;
                      if (!Number.isFinite(st2)) st2 = 0.5;
                      const pollRes = await storyRunService.ttsDialogOne({ storyRunId: run.id, index: ttsIndex, force: false, speaking_rate: sr2, stability: st2 });
                      if (pollRes?.processed || pollRes?.skipped) {
                        const url = pollRes?.audioUrl ?? null;
                        if (url) setAudioMap((prev) => ({ ...prev, [ttsIndex!]: url }));
                        // refresh plan data to update tts steps as well
                        try {
                          const plansDb = await storyImagePlanService.getPlansByRunId(run.id);
                          const aMap: Record<number, string | null> = {};
                          const stepsMap: Record<number, any[]> = {};
                          for (const raw of Array.isArray(plansDb) ? plansDb : []) {
                            const p: any = raw as any;
                            if (typeof p?.orderIndex === 'number') {
                              const k0 = p.orderIndex;
                              const k1 = p.orderIndex + 1;
                              for (const k of [k0, k1]) {
                                aMap[k] = (p?.audioUrl as string) || null;
                                const steps = Array.isArray((p as any).ttsSteps) ? (p as any).ttsSteps : [];
                                stepsMap[k] = steps;
                              }
                            }
                          }
                          setAudioMap(aMap);
                          setTtsStepsMap(stepsMap);
                        } catch {}
                        setMessageText('Seslendirme tamamlandı');
                        setMessageOpen(true);
                        setTtsOpen(false);
                        success = true;
                        break;
                      }
                    } catch (pe: any) {
                      const pm = pe?.response?.data?.message || pe?.message || '';
                      const stillInProgress = typeof pm === 'string' && pm.toLowerCase().includes('zaten devam ediyor');
                      if (!stillInProgress) {
                        // unexpected error -> break and show
                        break;
                      }
                    }
                    await sleep(5000);
                  }
                  if (!success) {
                    setMessageText('İşlem devam ediyor olabilir, lütfen daha sonra tekrar kontrol edin');
                    setMessageOpen(true);
                  }
                } else {
                  setMessageText(msg || 'İşlem başarısız');
                  setMessageOpen(true);
                }
              } finally {
                setTtsBusy(false);
              }
            }}
          >
            {ttsBusy ? 'Bekleyiniz...' : 'Evet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Step Re-TTS Confirm Dialog */}
      <Dialog open={redoStepOpen} onClose={() => !redoStepBusy && setRedoStepOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Yeniden Seslendir</DialogTitle>
        <DialogContent dividers>
          <Typography>Bu parçayı yeniden seslendirmek istiyor musunuz?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRedoStepOpen(false)} disabled={redoStepBusy}>Vazgeç</Button>
          <Button
            variant="contained"
            disabled={redoStepBusy || !redoStep}
            onClick={async () => {
              if (!redoStep) return;
              const { stepId, oi } = redoStep;
              setRedoStepBusy(true);
              try {
                const srRaw = speakingRateMap[oi];
                const stRaw = stabilityMap[oi];
                let sr = srRaw ? parseFloat(srRaw) : NaN;
                let st = stRaw ? parseFloat(stRaw) : NaN;
                if (!Number.isFinite(sr)) sr = 0.75;
                if (!Number.isFinite(st)) st = 0.5;
                const opts: any = { speaking_rate: sr, stability: st };
                const res = await storyRunService.ttsStepRedo(stepId, opts);
                if (res && res.audioUrl) {
                  setTtsStepsMap((prev) => {
                    const cur = Array.isArray(prev[oi]) ? prev[oi].slice() : [];
                    const idxFound = cur.findIndex((x: any) => x.id === stepId);
                    if (idxFound >= 0) {
                      cur[idxFound] = { ...cur[idxFound], audioUrl: res.audioUrl };
                    }
                    return { ...prev, [oi]: cur } as any;
                  });
                }
                setRedoStepOpen(false);
              } catch (e: any) {
                setMessageText(e?.response?.data?.message || e?.message || 'İşlem başarısız');
                setMessageOpen(true);
              } finally {
                setRedoStepBusy(false);
              }
            }}
          >
            {redoStepBusy ? 'Bekleyiniz...' : 'Evet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Run Confirmation */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Run</DialogTitle>
        <DialogContent dividers>
          <Typography>Silmek istediğinizden emin misiniz?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Vazgeç</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (!id) return;
              try {
                const res = await storyRunService.deleteRun(id);
                setMessageText(res?.message || 'Run deleted');
                setMessageOpen(true);
                setDeleteOpen(false);
                // Navigate to template detail if possible
                const target = run?.storyBaseId ? `/templates/${run.storyBaseId}` : -1;
                setTimeout(() => navigate(target as any), 300);
              } catch (e: any) {
                setMessageText(e?.response?.data?.message || e?.message || 'Silme başarısız');
                setMessageOpen(true);
                setDeleteOpen(false);
              }
            }}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={messageOpen} onClose={() => setMessageOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Info</DialogTitle>
        <DialogContent dividers>
          <Typography>{messageText}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setMessageOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* Start2 Confirm Dialog */}
      <Dialog open={start2Open} onClose={() => !start2Loading && setStart2Open(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Onay</DialogTitle>
        <DialogContent dividers>
          {precheckLoading ? (
            <Stack alignItems="center" spacing={1}>
              <CircularProgress size={24} />
              <Typography>Bilgiler inceleniyor</Typography>
            </Stack>
          ) : precheckHasData ? (
            <Alert severity="warning">
              Daha once Detayli Image Betimleme islemi yapilmis, yeniden yaparsaniz eski Image Betimler silinir ve yeni Betimlemeler eklenir
            </Alert>
          ) : (
            <Typography>Resimleri detayli betimlenecek.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStart2Open(false)} disabled={start2Loading}>İptal</Button>
          <Button
            variant="contained"
            disabled={start2Loading || !run?.id}
            onClick={async () => {
              if (!run?.id) return;
              setStart2Loading(true);
              setLoading(true);
              try {
                await storyRunService.startRun2(run.id);
                setMessageText('Resim Betimleme tamamlandı');
                setMessageOpen(true);
                setStart2Open(false);
              } catch (e: any) {
                setMessageText(e?.response?.data?.message || e?.message || 'İşlem başarısız');
                setMessageOpen(true);
              } finally {
                setStart2Loading(false);
                setLoading(false);
              }
            }}
          >
            {start2Loading ? 'Bekleyiniz...' : 'Devam'}
          </Button>
        </DialogActions>
      </Dialog>
      {!loading && error && (
        <Alert severity="error">{error}</Alert>
      )}
      {!loading && !error && run && (
        <Stack spacing={2}>
          {/* Global render settings (video-level) */}
          {run.id && (
            <Accordion expanded={globalExpanded} onChange={(_, isExp) => setGlobalExpanded(isExp)} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Global Render Ayarları</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <GlobalRenderSettingsForm runId={run.id} storyTarget={storyTarget as any} />
              </AccordionDetails>
            </Accordion>
          )}

          <Paper sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
              <Chip label={run.status} size="small" />
              <Chip label={run.mode} size="small" />
              {typeof run.runNumber !== 'undefined' && (
                <Chip label={`#${run.runNumber}`} size="small" />
              )}
              <Chip label={`Images: ${run.imageCount ?? 0}`} size="small" />
              {typeof run.estimatedDurationMs === 'number' && (
                <Chip label={`Duration: ${formatMs(run.estimatedDurationMs)}`} size="small" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {new Date(run.createdAt).toLocaleString()}
            </Typography>
            {run.baseImageUrl && (
              <Box sx={{ mt: 2 }}>
                <img
                  src={run.baseImageUrl}
                  alt="base"
                  style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain', borderRadius: 8 }}
                />
              </Box>
            )}
            <Divider sx={{ my: 2 }} />
            <Stack spacing={0.5}>
              <Typography variant="body2"><strong>Run ID:</strong> {run.id}</Typography>
              <Typography variant="body2"><strong>Run Number:</strong> {String(run.runNumber ?? '')}</Typography>
              <Typography variant="body2"><strong>Base Story ID:</strong> {run.storyBaseId}</Typography>
            </Stack>
            {run.userNote && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">User Note</Typography>
                <Box sx={{ whiteSpace: 'pre-wrap' }}>
                  <Typography variant="body2">{run.userNote}</Typography>
                </Box>
              </Box>
            )}
          </Paper>

          {/* Action before Image Plans */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" variant="contained" onClick={() => setStart2Open(true)}>
              Detaylı Betimle
            </Button>
          </Box>


          {/* Image Plans - collapsible */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Image Plans</Typography>
            <Divider sx={{ mb: 1 }} />
            {Array.isArray(plans) && plans.length > 0 ? (
              <Stack>
                {plans.map((p: any, idx: number) => {
                  const header = `Scene #${p.index ?? idx + 1} — ${formatMs(p.durationMs)} @ ${formatMs(p.startMs)}`;
                  return (
                    <Accordion
                      key={idx}
                      expanded={expandedIdx === idx}
                      onChange={(_, isExp) => setExpandedIdx(isExp ? idx : null)}
                      disableGutters
                      sx={{
                        '& .MuiAccordionSummary-root.Mui-expanded': {
                          backgroundColor: 'rgba(173, 216, 230, 0.25)'
                        },
                        '& .MuiAccordionDetails-root': {
                          backgroundColor: 'rgba(173, 216, 230, 0.25)'
                        }
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack sx={{ width: '100%' }}>
                          <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{header}</Typography>
                          {p.sceneDescription && editingIdx !== idx && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              title={p.sceneDescription}
                              sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                            >
                              {p.sceneDescription}
                              {/* {((p.sceneDescription as string) || '').length > 100
                                ? `${(p.sceneDescription as string).slice(0, 100)}…`
                                : (p.sceneDescription as string) || ''} */}
                            </Typography>
                          )}
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        {/* Action buttons row moved inside details */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                          {(() => {
                            const oi = idx + 1;
                            const has = !!audioMap[oi];
                            const endIcon = has
                              ? <CheckCircleOutlineIcon sx={{ color: 'success.main' }} />
                              : <VolumeOffOutlinedIcon color="disabled" />;
                            return (
                              <Button
                                size="small"
                                endIcon={endIcon}
                                onClick={() => {
                                  setTtsIndex(oi);
                                  setTtsHasAudio(has);
                                  setTtsOpen(true);
                                }}
                              >
                                Seslendir
                              </Button>
                            );
                          })()}
                          {/* Per-scene TTS params */}
                          {(() => {
                            const oi = idx + 1;
                            return (
                              <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                  label="speaking_rate"
                                  type="number"
                                  inputProps={{ min: 0.01, max: 1, step: 0.01 }}
                                  value={speakingRateMap[oi] ?? '0.75'}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setSpeakingRateMap((prev) => ({ ...prev, [oi]: v }));
                                  }}
                                  placeholder="0.75"
                                  sx={{ width: 140 }}
                                />
                                <TextField
                                  label="stability"
                                  type="number"
                                  inputProps={{ min: 0.01, max: 1, step: 0.01 }}
                                  value={stabilityMap[oi] ?? '0.5'}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setStabilityMap((prev) => ({ ...prev, [oi]: v }));
                                  }}
                                  placeholder="0.5"
                                  sx={{ width: 140 }}
                                />
                              </Stack>
                            );
                          })()}
                          {(() => {
                            const oi = idx + 1;
                            return (
                              <Button
                                size="small"
                                onClick={() => {
                                  setUpIndex(oi);
                                  setUpHasExisting(!!upscaledMap[oi]);
                                  setUpHasBaseImage(!!(imageMap[oi] || userImageMap[oi]));
                                  setUpOpen(true);
                                }}
                              >
                                Cre.Upscale
                              </Button>
                            );
                          })()}
                          {(() => {
                            const oi = idx + 1;
                            const up2x = upscaledMap[oi];
                            const endIcon = up2x
                              ? <CheckCircleOutlineIcon sx={{ color: 'success.main' }} />
                              : <RadioButtonUncheckedIcon color="disabled" />;
                            return (
                              <Button
                                size="small"
                                endIcon={endIcon}
                                onClick={up2x ? () => { const sep = up2x.includes('?') ? '&' : '?'; window.open(`${up2x}${sep}v=${Date.now()}`,'_blank','noopener,noreferrer'); } : undefined}
                              >
                                Upscale
                              </Button>
                            );
                          })()}
                          {editingIdx !== idx && (
                            <Button
                              size="small"
                              onClick={() => {
                                setPlanBackup(JSON.parse(JSON.stringify(plans[idx] || {})));
                                setEditingIdx(idx);
                              }}
                            >
                              Edit
                            </Button>
                          )}
                        </Box>
                        {editingIdx === idx ? (
                          <Stack spacing={1.5}>
                            {(() => {
                              const oi = idx + 1;
                              const planId = planIdMap[oi];
                              return planId ? (
                                <SceneAdvancedSettings planId={planId} />
                              ) : null;
                            })()}
                            <Stack direction="row" spacing={1}>
                              <TextField label="Start (ms)" type="number" value={p.startMs ?? ''} onChange={(e) => {
                                const next = [...plans];
                                next[idx] = { ...next[idx], startMs: e.target.value ? Number(e.target.value) : undefined };
                                setPlans(next);
                              }} sx={{ maxWidth: 200 }} />
                              <TextField label="Duration (ms)" type="number" value={p.durationMs ?? ''} onChange={(e) => {
                                const next = [...plans];
                                next[idx] = { ...next[idx], durationMs: e.target.value ? Number(e.target.value) : undefined };
                                setPlans(next);
                              }} sx={{ maxWidth: 200 }} />
                            </Stack>
                            <TextField
                              label="Scene Description"
                              value={p.sceneDescription ?? ''}
                              onChange={(e) => {
                                const next = [...plans];
                                next[idx] = { ...next[idx], sceneDescription: e.target.value };
                                setPlans(next);
                              }}
                              fullWidth
                              multiline
                              minRows={2}
                            />
                            <TextField
                              label="Story Excerpt"
                              value={p.storyExcerpt ?? ''}
                              onChange={(e) => {
                                const next = [...plans];
                                next[idx] = { ...next[idx], storyExcerpt: e.target.value };
                                setPlans(next);
                              }}
                              fullWidth
                              multiline
                              minRows={2}
                            />
                            {Array.isArray(p.dialogs) && (
                              <Stack spacing={1}>
                                <Typography variant="caption" color="text.secondary">Dialogs</Typography>
                                {p.dialogs.map((d: any, di: number) => (
                                  <Stack key={di} spacing={1}>
                                    <TextField label="Speaker" value={d.speakerRole ?? ''} InputProps={{ readOnly: true }} sx={{ maxWidth: 140 }} />
                                    <TextField label="Text" value={d.text ?? ''} onChange={(e) => {
                                      const next = [...plans];
                                      const dlg = [...(next[idx].dialogs || [])];
                                      dlg[di] = { ...dlg[di], text: e.target.value };
                                      next[idx] = { ...next[idx], dialogs: dlg };
                                      setPlans(next);
                                    }} fullWidth multiline minRows={2} />
                                    <Divider />
                                  </Stack>
                                ))}
                              </Stack>
                            )}
                            <Divider sx={{ my: 1 }} />
                            {(() => {
                              const oi = Number(p?.index ?? idx + 1);
                              const planId = planIdMap[oi];
                              return planId ? (
                                <Stack spacing={2}>
                                  <ScenePlanForm planId={planId} onSaved={(saved) => {
                                    // reflect audioUrl or visuals if returned, otherwise ignore
                                  }} />
                                  <TtsStepsForm imagePlanId={planId} />
                                </Stack>
                              ) : null;
                            })()}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                              <Button
                                size="small"
                                onClick={() => {
                                  if (planBackup) {
                                    const next = [...plans];
                                    next[idx] = planBackup;
                                    setPlans(next);
                                  }
                                  setEditingIdx(null);
                                  setPlanBackup(null);
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={async () => {
                                  if (!id) return;
                                  try {
                                    const res = await storyRunService.updateRun(id, { imagePlanJson: plans });
                                    setMessageText(res?.message || 'Scene updated');
                                    setMessageOpen(true);
                                    setEditingIdx(null);
                                    setPlanBackup(null);
                                  } catch (e: any) {
                                    setMessageText(e?.response?.data?.message || e?.message || 'Güncelleme başarısız');
                                    setMessageOpen(true);
                                  }
                                }}
                              >
                                Save
                              </Button>
                            </Box>
                          </Stack>
                        ) : (
                          <Stack spacing={1.5}>
                            {(() => {
                              const oi = Number(p?.index ?? idx + 1);
                              const planId = planIdMap[oi];
                              return planId ? (
                                <SceneAdvancedSettings planId={planId} />
                              ) : null;
                            })()}
                            <Stack direction="row" spacing={2}>
                              <Typography variant="body2"><strong>Start:</strong> {p.startMs}</Typography>
                              <Typography variant="body2"><strong>Duration:</strong> {p.durationMs}</Typography>
                            </Stack>
                            {(() => {
                              const oi = idx + 1;
                              const thumb = thumbMap[oi];
                              const full = imageMap[oi];
                              if (!thumb) return null;
                              return (
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">Image</Typography>
                                  <Box sx={{ mt: 0.5 }}>
                                    <img
                                      src={thumb}
                                      alt="thumbnail"
                                      style={{ maxWidth: 280, width: '100%', height: 'auto', borderRadius: 8, cursor: full ? 'pointer' : 'default' }}
                                      onClick={() => {
                                        if (full) { setImgPreviewUrl(full); setImgPreviewOpen(true); }
                                      }}
                                    />
                                  </Box>
                                </Box>
                              );
                            })()}
                            {(() => {
                              const oi = idx + 1;
                              const planId = planIdMap[oi];
                              const userThumb = userThumbMap[oi];
                              const userFull = userImageMap[oi];
                              const localPrev = userLocalPreviewMap[oi];
                              return (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary">User Image</Typography>
                                  <Stack spacing={1} sx={{ mt: 0.5 }}>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        const url = URL.createObjectURL(file);
                                        setCropImageUrl(url);
                                        setCropIndex(oi);
                                        setCropOpen(true);
                                      }}
                                    />
                                    {(localPrev || userThumb) && (
                                      <Box>
                                        <img
                                          src={localPrev || userThumb || ''}
                                          alt="user-thumb"
                                          style={{ maxWidth: 280, width: '100%', height: 'auto', borderRadius: 8, cursor: (userFull || localPrev) ? 'pointer' : 'default' }}
                                          onClick={() => {
                                            const fullUrl = userFull || localPrev;
                                            if (fullUrl) { setImgPreviewUrl(fullUrl); setImgPreviewOpen(true); }
                                          }}
                                        />
                                      </Box>
                                    )}
                                    <Stack direction="row" spacing={1}>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        disabled={!userLocalFileMap[oi]}
                                        onClick={async () => {
                                          let pid = planId;
                                          const file = userLocalFileMap[oi];
                                          if (!file) return;
                                          try {
                                            if (!pid && run?.id) {
                                              // Refetch plans and rebuild maps to resolve missing planId
                                              const plansDb = await storyImagePlanService.getPlansByRunId(run.id);
                                              const pidMap: Record<number, string> = {};
                                              for (const raw of Array.isArray(plansDb) ? plansDb : []) {
                                                const p: any = raw as any;
                                                if (typeof p?.orderIndex === 'number' && p.id) {
                                                  pidMap[p.orderIndex] = p.id;
                                                  pidMap[p.orderIndex + 1] = p.id;
                                                }
                                              }
                                              setPlanIdMap((prev) => ({ ...prev, ...pidMap }));
                                              pid = pidMap[oi] || pid;
                                            }
                                            if (!pid) {
                                              setMessageText('Plan bulunamadı, lütfen sayfayı yenileyip tekrar deneyin.');
                                              setMessageOpen(true);
                                              return;
                                            }
                                            const updated = await storyImagePlanService.uploadUserImage(pid, file, oi);
                                            setUserThumbMap((prev) => ({ ...prev, [oi]: updated.userImageThumbnailUrl || null }));
                                            setUserImageMap((prev) => ({ ...prev, [oi]: updated.userImageUrl || null }));
                                            setUserLocalPreviewMap((prev) => { const c = { ...prev }; delete c[oi]; return c; });
                                            setUserLocalFileMap((prev) => { const c = { ...prev } as any; delete c[oi]; return c; });
                                            setMessageText('User image saved');
                                            setMessageOpen(true);
                                          } catch (e: any) {
                                            setMessageText(e?.response?.data?.message || e?.message || 'Upload failed');
                                            setMessageOpen(true);
                                          }
                                        }}
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        size="small"
                                        color="error"
                                        disabled
                                      >
                                        Delete (pasif)
                                      </Button>
                                    </Stack>
                                  </Stack>
                                </Box>
                              );
                            })()}
                            {(() => {
                              const oi = idx + 1;
                              const desc = descriptionMap[oi];
                              if (!desc) return null;
                              return (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Image-Detail</Typography>
                                  <Box sx={{ whiteSpace: 'pre-wrap' }}>
                                    <Typography variant="body2">{desc}</Typography>
                                  </Box>
                                  <Box sx={{ mt: 1 }}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      // disabled={!!aiBusyMap[oi] || !planIdMap[oi]}
                                       disabled={true}
                                      onClick={() => {
                                        setAiCreateIndex(oi);
                                        setAiCreateOpen(true);
                                      }}
                                    >
                                      Create Image From AI
                                    </Button>
                                  </Box>
                                </Box>
                              );
                            })()}
                            {(() => {
                              const oi = idx + 1;
                              const src = audioMap[oi];
                              if (!src) return null;
                              return (
                                <Stack spacing={0.5}>
                                  <Typography variant="body2" color="text.secondary">Ses önizleme</Typography>
                                  <audio
                                    ref={(el) => { audioRefs.current[oi] = el; }}
                                    src={src}
                                    preload="metadata"
                                    controls
                                    style={{ width: 360, maxWidth: '100%' }}
                                    onLoadedMetadata={(e) => {
                                      const d = (e.currentTarget as HTMLAudioElement).duration;
                                      setAudioDurMap((prev) => ({ ...prev, [oi]: Number.isFinite(d) ? d : 0 }));
                                    }}
                                    onTimeUpdate={(e) => {
                                      const t = (e.currentTarget as HTMLAudioElement).currentTime;
                                      setAudioPosMap((prev) => ({ ...prev, [oi]: Number.isFinite(t) ? t : 0 }));
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatTime(audioPosMap[oi])} / {formatTime(audioDurMap[oi])}
                                  </Typography>
                                </Stack>
                              );
                            })()}
                            {(() => {
                              const oi = idx + 1;
                              const stepsRaw = Array.isArray(ttsStepsMap[oi]) ? ttsStepsMap[oi] : [];
                              const steps = stepsRaw.slice().sort((a: any, b: any) => {
                                const ao = typeof a?.orderIndex === 'number' ? a.orderIndex : 0;
                                const bo = typeof b?.orderIndex === 'number' ? b.orderIndex : 0;
                                return ao - bo;
                              });
                              if (!steps || steps.length === 0) return null;
                              return (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary">Parça Sesler</Typography>
                                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                    {steps.map((s: any, si: number) => (
                                      <Stack key={si} spacing={0.5}>
                                        <Typography variant="body2">
                                          <strong>#{s.orderIndex ?? si + 1}</strong>
                                          {s.speaker ? ` · Speaker ${s.speaker}` : ''}
                                          {s.character ? ` · ${s.character}` : ''}
                                        </Typography>
                                        {s.text && (
                                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {s.text}
                                          </Typography>
                                        )}
                                        <Stack direction="row" spacing={1} alignItems="center">
                                          {s.audioUrl && (
                                            <audio src={s.audioUrl} preload="none" controls style={{ width: 360, maxWidth: '100%' }} />
                                          )}
                                          <IconButton size="small" title="Düzenle"
                                            onClick={() => {
                                              setEditModalStep({
                                                id: s.id,
                                                orderIndex: s.orderIndex,
                                                speaker: s.speaker ?? null,
                                                character: s.character ?? null,
                                                text: s.text ?? null,
                                                audioUrl: s.audioUrl ?? null,
                                              });
                                              setEditModalOpen(true);
                                            }}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            title="Yeniden Seslendir"
                                            onClick={() => {
                                              const oi = idx + 1;
                                              setRedoStep({ stepId: s.id, oi });
                                              setRedoStepOpen(true);
                                            }}
                                          >
                                            <ReplayIcon fontSize="small" />
                                          </IconButton>
                                        </Stack>
                                        <Divider />
                                      </Stack>
                                    ))}
                                  </Stack>
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={async () => {
                                        const planId = planIdMap[oi];
                                        if (!planId) return;
                                        try {
                                          setRedoBusy(true);
                                          const res = await storyImagePlanService.recombineTts(planId);
                                          if (res && res.audioUrl) {
                                            setAudioMap((prev) => ({ ...prev, [oi]: res.audioUrl }));
                                            setMessageText('Ses parçaları birleştirildi');
                                            setMessageOpen(true);
                                          }
                                        } catch (e: any) {
                                          setMessageText(e?.response?.data?.message || e?.message || 'Sesleri birleştirme başarısız');
                                          setMessageOpen(true);
                                        } finally {
                                          setRedoBusy(false);
                                        }
                                      }}
                                    >
                                      Sesleri Yeniden Birlestir
                                    </Button>
                                  </Box>
                                </Box>
                              );
                            })()}
                            {p.sceneDescription && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">Scene Description</Typography>
                                <Box sx={{ whiteSpace: 'pre-wrap' }}>
                                  <Typography variant="body2">{p.sceneDescription}</Typography>
                                </Box>
                              </Box>
                            )}
                            {p.storyExcerpt && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">Story Excerpt</Typography>
                                <Box sx={{ whiteSpace: 'pre-wrap' }}>
                                  <Typography variant="body2">{p.storyExcerpt}</Typography>
                                </Box>
                              </Box>
                            )}
                            {Array.isArray(p.dialogs) && p.dialogs.length > 0 && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">Dialogs</Typography>
                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                  {p.dialogs.map((d: any, di: number) => (
                                    <Typography key={di} variant="body2">
                                      <strong>{d.speakerRole}:</strong> {d.text}
                                    </Typography>
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </Stack>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">Plan bulunamadı.</Typography>
            )}
          </Paper>

          {/* analysisRawJson bilerek gösterilmiyor */}
        </Stack>
      )}
      <Backdrop open={redoBusy} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Stack alignItems="center" spacing={1}>
          <CircularProgress color="inherit" />
          <Typography>İşlem yapılıyor...</Typography>
        </Stack>
      </Backdrop>
      <TtsStepEditModal
        open={editModalOpen}
        step={editModalStep}
        onClose={() => setEditModalOpen(false)}
        onReplace={async (file) => {
          if (!editModalStep?.id) return;
          const res = await storyRunService.ttsStepReplace(editModalStep.id, file);
          if (res && res.audioUrl) {
            setTtsStepsMap((prev) => {
              // As steps are mapped by oi in current list, scan all entries to find and update by id
              const next: Record<number, any[]> = { ...prev } as any;
              for (const k of Object.keys(next)) {
                const arr = Array.isArray(next[k as any]) ? next[k as any].slice() : [];
                const idxFound = arr.findIndex((x: any) => x.id === editModalStep.id);
                if (idxFound >= 0) {
                  arr[idxFound] = { ...arr[idxFound], audioUrl: res.audioUrl };
                  next[k as any] = arr;
                  break;
                }
              }
              return next;
            });
          }
          setEditModalOpen(false);
        }}
      />
      <ImageCropDialog
        open={cropOpen}
        imageUrl={cropImageUrl}
        outputWidth={storyTarget === 'short' ? 864 : 1536}
        outputHeight={storyTarget === 'short' ? 1536 : 864}
        onCancel={() => {
          setCropOpen(false);
          setCropImageUrl(null);
        }}
        onSave={async (file, previewUrl) => {
          try {
            if (cropIndex != null) {
              const oi = cropIndex;
              setUserLocalFileMap((prev) => ({ ...prev, [oi]: file }));
              setUserLocalPreviewMap((prev) => ({ ...prev, [oi]: previewUrl }));
            }
          } finally {
            setCropOpen(false);
            setCropImageUrl(null);
          }
        }}
      />
    </Box>
  );
};

export default RunDetailPage;
